import * as React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { useMutation } from '@apollo/client';

import { ModalContext, ModalModel, Text } from '@notino/react-styleguide';
import {
  SendWatchdogMutation,
  SendWatchdogMutationVariables,
} from '@notino/shared/definitions/types';

import { trackWatchdogSent } from '@sharedComponents/Watchdog/tracking';

import {
  WatchdogComm,
  IWatchdogState,
  IWatchdogFields,
  IWatchdogProps,
} from './model';
import sendWatchdogMutation from './queries/sendWatchdog.graphql';
import { Paragraph } from './styled';
import { WatchdogForm } from './WatchdogForm';
import { watchdogMessages } from './watchdogMessages';

type Props = { onSuccess: () => void } & Pick<IWatchdogProps, 'variant'>;

export const WatchdogModal: React.FC<Props> = ({ variant, onSuccess }) => {
  const { formatMessage } = useIntl();
  const { hideModal, toggleModal } = ModalContext.useModalContext();
  const [state, setState] = React.useState<IWatchdogState>({
    errors: [],
    status: WatchdogComm.IDLE,
    submitAttempts: 0,
  });

  const [saveWatchdog] = useMutation<
    SendWatchdogMutation,
    SendWatchdogMutationVariables
  >(sendWatchdogMutation, {
    onError: () => {
      setState((prevState) => ({
        ...prevState,
        status: WatchdogComm.ERROR,
      }));
    },
    onCompleted: (data) => {
      if (!data) {
        return;
      }

      const { errors, success } = data.sendWatchdog;
      if (errors || !success) {
        setState((prevState) => ({
          ...prevState,
          errors: errors || [],
          status: WatchdogComm.ERROR,
        }));
        return;
      }

      onSuccess();

      setState((prevState) => ({
        ...prevState,
        status: WatchdogComm.IDLE,
      }));

      toggleModal(
        <Paragraph marginBottom="0">
          <FormattedMessage {...watchdogMessages.watchdogSentText} />
        </Paragraph>,
        {
          header: (
            <Text typography="titleLarge">
              <FormattedMessage {...watchdogMessages.watchdogSentHeader} />
            </Text>
          ),
          type: ModalModel.Types.default,
          positionBottomOnMobile: true,
          withFocusTrap: true,
        }
      );
      setTimeout(() => {
        hideModal();
      }, 5000);
    },
  });

  const handleSubmit = React.useCallback(
    async (values: IWatchdogFields) => {
      setState((prevState) => ({
        ...prevState,
        status: WatchdogComm.SENDING,
        submitAttempts: prevState.submitAttempts + 1,
      }));

      try {
        await saveWatchdog({
          variables: {
            email: values.watchdogEmail,
            productId: variant.id.toString(),
          },
        });
        trackWatchdogSent(values.watchdogEmail, variant);
      } catch (_) {
        setState((prevState) => ({
          ...prevState,
          status: WatchdogComm.ERROR,
        }));
      }
    },
    [saveWatchdog, variant]
  );

  const errorMessage =
    state.status === WatchdogComm.ERROR
      ? formatMessage(watchdogMessages.watchdogSubmitError)
      : undefined;

  return (
    <WatchdogForm
      handleSubmit={handleSubmit}
      errorMessage={
        state.errors.length === 0 ? errorMessage : state.errors.join(' ')
      }
      submitAttempts={state.submitAttempts}
      status={state.status}
    />
  );
};
